import { useEffect, useRef, useState } from "react";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { OilIntakeLogModel } from "../../models/oilProduction-model";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import EditProductionLogDialog from "./EditProductionaLogDialog";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import { MenuType } from "../../types/enum";
import { usePageNavigation } from "../user-menu/menu/navigation";


interface FilterParams {
  keywordSearch?: string | null;
  oilSeedID?: number | null;
  posID?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface SortParams {
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc" | null;
}

interface Props {
  data?: OilIntakeLogModel[];
  setLoading: (loading: boolean) => void;
  totalRecords?: number;
  onPageChange?: (pageNumber: number, pageSize: number) => void;
  onSortChange?: (sortColumn: string | null, sortDirection: "asc" | "desc" | null) => void;
  pageNumber?: number;
  pageSize?: number;
  filters?: FilterParams;
  sortParams?: SortParams;
  onDataRefresh?: () => void;
}

const OilProductionDataTable: React.FC<Props> = ({
  data,
  setLoading,
  totalRecords = 0,
  onPageChange,
  onSortChange,
  pageNumber = 1,
  pageSize = 10,
  filters = {},
  sortParams = {},
  onDataRefresh
}) => {
  const [records, setRecords] = useState<OilIntakeLogModel[]>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<OilIntakeLogModel | null>(null);
  const toast = useRef<Toast>(null);
  const navigateToPage = usePageNavigation();


  const startIndex = totalRecords > 0 ? (pageNumber - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(pageNumber * pageSize, totalRecords);

  const sortFieldMapping: { [key: string]: string } = {
    'vendorname': 'vendorname',
    'invoicenumber': 'invoicenumber',
    'gaananame': 'gaananame',
    'oilseedname': 'oilseedname',
    'submitteddate': 'submitteddate',
    'posname': 'posname',
    'oilSeedWeight': 'oilseedweight',
    'moistureContent': 'moisturecontent',
    'emptyContainerWeightKG': 'emptycontainerweight',
    'startTime': 'starttime',
    'endTime': 'endtime',
    'operator1Name': 'operator1name',
    'operator2Name': 'operator2name',
    'bull1Name': 'bull1name',
    'bull2Name': 'bull2name',
    'dailyOutputLitres': 'dailyoutputlitres',
    'inputToOutputRatio': 'inputtooutputratio'
  };

  const paginatorTemplate = {
    layout: "RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport  NextPageLink LastPageLink ",
    CurrentPageReport: () => (
      <span className="mx-3 text-sm text-gray-600">
        {startIndex} to {endIndex} of {totalRecords}
      </span>
    ),
  };

  useEffect(() => {
    if (Array.isArray(data)) {
      setRecords([...data]);
    }
  }, [data]);

  const handlePageChange = (e: DataTablePageEvent) => {
    const newPageNumber = e.page! + 1;
    const newPageSize = e.rows;

    if (onPageChange) {
      onPageChange(newPageNumber, newPageSize);
    }
  };

  const handleSort = (e: DataTableSortEvent) => {
    if (onSortChange && e.sortField) {
      const backendSortField = sortFieldMapping[e.sortField] || e.sortField;
      const sortDirection = e.sortOrder === 1 ? "asc" : "desc";

      onSortChange(backendSortField, sortDirection);
    } else if (onSortChange) {
      onSortChange(null, null);
    }
  };

  const handleEditClick = async (row: OilIntakeLogModel) => {
    setLoading(true);
    try {
      const existsInBatch = await oilProductionService.checkIfOilIntakeExistsInBatch(row.id!);

      if (existsInBatch) {
        // Show validation message if record exists in batch
        toast.current?.show({
          severity: "warn",
          summary: "Edit Restricted",
          detail: "This oil intake log is already assigned to a batch and cannot be edited.",
          life: 5000,
        });
        setLoading(false);
        return;
      }

      // Allow edit if record doesn't exist in batch
      setSelectedRow(row);
      setShowEditDialog(true);
    } catch (error) {
      console.error("Error checking batch status:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to check if record can be edited.",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = async () => {
    setShowEditDialog(false);
    setSelectedRow(null);

    if (onDataRefresh) {
      onDataRefresh();
    }

    navigateToPage(MenuType.OilProductionRecords);
  };

  const handleEditCancel = () => {
    setShowEditDialog(false);
    setSelectedRow(null);
  };
  const formatOilLogNumber = (id?: number) => {
    if (!id) return "-";
    return `OIL${id.toString().padStart(8, "0")}`;
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="col-12">
        <div className="card border-noround p-2">
          <DataTable
            value={records}
            paginator
            lazy
            rows={pageSize}
            first={(pageNumber - 1) * pageSize}
            totalRecords={totalRecords}
            paginatorTemplate={paginatorTemplate}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPage={handlePageChange}
            onSort={handleSort}
            sortField={sortParams.sortColumn || ''}
            sortOrder={sortParams.sortDirection === 'asc' ? 1 : sortParams.sortDirection === 'desc' ? -1 : 0}
            removableSort
          >
            <Column
              field="id"
              header="Oil Intake Log #"
              body={(rowData) => formatOilLogNumber(rowData.id)}
            />

            <Column
              field="submittedDate"
              header="Production Date"
              body={(rowData) => {
                if (!rowData.submittedDate) return "";
                const date = new Date(rowData.submittedDate);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
              }}
              sortable
            />
            <Column field="gaanaName" header="Gaana Used" sortable />
            <Column
              field="batch"
              header="Batch"
              body={(rowData) => rowData.batch || "-"}
            />
            <Column
              field="startTime"
              header="Start Time"
              body={(rowData) => {
                if (!rowData.startTime) return "";
                const time = new Date(`1970-01-01T${rowData.startTime}`);
                const hours = String(time.getHours()).padStart(2, "0");
                const minutes = String(time.getMinutes()).padStart(2, "0");
                return `${hours}:${minutes}`;
              }}
            />
            <Column
              field="endTime"
              header="End Time"
              body={(rowData) => {
                if (!rowData.endTime) return "";
                const time = new Date(`1970-01-01T${rowData.endTime}`);
                const hours = String(time.getHours()).padStart(2, "0");
                const minutes = String(time.getMinutes()).padStart(2, "0");
                return `${hours}:${minutes}`;
              }}
            />
            <Column field="posName" header="Manufacture Unit" sortable />
            {/* <Column field="gaanaName" header="Gaana Used" sortable /> */}
            <Column field="oilSeedName" header="Oilseed Name" sortable />
            <Column field="oilSeedWeight" header="Seed Weight (kg)" />
            <Column field="moistureContent" header="Moisture Content" />
            <Column field="emptyContainerWeightKG" header="Empty Container Weight(kg)" />
            <Column
              header="Total Oil Weight (kg)"
              body={(rowData) => {
                if (rowData.dailyOutputs && rowData.dailyOutputs.length > 0) {
                  return rowData.dailyOutputs[0].totalOilWeightWithContainerKG ?? "-";
                }
                return "-";
              }}
            />
            <Column
              header="Cake Weight (kg)"
              body={(rowData) => {
                if (rowData.cakeOutputs && rowData.cakeOutputs.length > 0) {
                  return rowData.cakeOutputs[0].cakeWeightKG ?? "-";
                }
                return "-";
              }}
            />
            <Column
              header="Residue Container Weight (kg)"
              body={(rowData) => {
                if (rowData.cakeOutputs && rowData.cakeOutputs.length > 0) {
                  return rowData.cakeOutputs[0].residueContainerWeightKG ?? "-";
                }
                return "-";
              }}
            />
            <Column
              header="Residue Weight (kg)"
              body={(rowData) => {
                if (rowData.cakeOutputs && rowData.cakeOutputs.length > 0) {
                  return rowData.cakeOutputs[0].residueWeightKG ?? "-";
                }
                return "-";
              }}
            />
            <Column
              header="Standard Container and Bottom Maddi Weight (kg)"
              body={(rowData) => {
                if (rowData.dailyOutputs && rowData.dailyOutputs.length > 0) {
                  return rowData.dailyOutputs[0].maddiWeightwithContainer ?? "-";
                }
                return "-";
              }}
            />
            <Column
              header="Actual Maddi Weight with Container (kg)"
              body={(rowData) => {
                if (rowData.dailyOutputs && rowData.dailyOutputs.length > 0) {
                  return rowData.dailyOutputs[0].containerandBottomMaddiWeight ?? "-";
                }
                return "-";
              }}
            />
            <Column
              header="Maddi Weight (kg)"
              body={(rowData) => {
                if (rowData.dailyOutputs && rowData.dailyOutputs.length > 0) {
                  return rowData.dailyOutputs[0].maddiWeight ?? "-";
                }
                return "-";
              }}
            />
            <Column field="operator1Name" header="Operator 1" />
            <Column field="operator2Name" header="Operator 2" />
            <Column field="bull1Name" header="Bull 1" />
            <Column field="bull2Name" header="Bull 2" />
            <Column
              header="Daily OutputLtrs"
              body={(rowData) => {
                if (rowData.dailyOutputs && rowData.dailyOutputs.length > 0) {
                  const dailyOutput = rowData.dailyOutputs[0].dailyOutputLitres;
                  return dailyOutput !== null && dailyOutput !== undefined
                    ? dailyOutput.toFixed(2)
                    : "-";
                }
                return "-";
              }}
            />
            <Column
              header="Input To Output Ratio"
              body={(rowData) => {
                if (rowData.dailyOutputs && rowData.dailyOutputs.length > 0) {
                  const ratio = rowData.dailyOutputs[0].inputToOutputRatio;
                  if (ratio !== null && ratio !== undefined) {
                    const percentage = (ratio * 100).toFixed(2);
                    return `${percentage}%`;
                  }
                }
                return "-";
              }}
            />
            <Column field="vendorName" header="Vendor" sortable />
            <Column
              header="Edit"
              body={(rowData) => (
                <div style={{ minWidth: "35px", display: "flex", justifyContent: "center" }}>
                  <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-warning"
                    aria-label="Edit"
                    onClick={() => handleEditClick(rowData)}
                    style={{ width: "2rem", height: "2rem" }}
                  />
                </div>
              )}
              frozen
              alignFrozen="right"
              headerStyle={{ textAlign: "center" }}
            />
          </DataTable>
        </div>

        <EditProductionLogDialog
          visible={showEditDialog}
          selectedRow={selectedRow}
          toastRef={toast}
          onSuccess={handleEditSuccess}
          onCancel={handleEditCancel}
          onDataRefresh={onDataRefresh}
        />
      </div>
    </>
  );
};

export default OilProductionDataTable;